import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ormCalculations from "../../../hooks/api/calculate";
import ORMfunctions from "../../../hooks/app/general";
import { type RootState } from "../../../store/store";
import {
  setTariffsSuccess,
  setTariffsLoading,
  setTariffsError,
  setStartTime,
  setEndTime,
  setStartDate,
  setEndDate,
} from "../../../store/orm/userSession";
import { convertCurrency } from "../../../utils/formater";
import { Vehicle } from "../../../widgets/Tariffs";
import {
  setSelectedVehicle,
  setSelectedStation,
} from "../../../store/orm/userSession";
import ormStationsService from "../../../hooks/api/stations";

// Function to parse URL parameters
function getUrlParams(url: string) {
  let params = {} as any;
  let parser = document.createElement("a");
  parser.href = url;
  let query = parser.search.substring(1);
  let vars = query.split("&");
  vars.forEach(function (v) {
    let pair = v.split("=") as any;
    params[pair[0]] = decodeURIComponent(pair[1]);
  });
  return params;
}

// Function to get the date string in the format YYYY-MM-DD
function getFormattedDate(date: any) {
  let year = date.getFullYear();
  let month = ("0" + (date.getMonth() + 1)).slice(-2);
  let day = ("0" + date.getDate()).slice(-2);
  return `${year}-${month}-${day}`;
}

// Function to get the next weekday date
function getNextWeekdayDate(weekday: any, baseDate = new Date()) {
  let currentDay = baseDate.getDay(); // 0 (Sunday) to 6 (Saturday)
  let targetDay = parseInt(weekday); // 1 (Monday) to 7 (Sunday)

  // Convert Sunday from 0 to 7 for consistency with 1-7 (Monday-Sunday)
  currentDay = currentDay === 0 ? 7 : currentDay;

  let dayDifference = targetDay - currentDay;
  if (dayDifference <= 0) {
    dayDifference += 7;
  }

  let targetDate = new Date(
    baseDate.getTime() + dayDifference * 24 * 60 * 60 * 1000,
  );
  return targetDate;
}

// Function to apply delay in hours to a given date
function applyHourDelay(date: any, delay: any) {
  date.setHours(date.getHours() + parseInt(delay));
  return date;
}

function roundUpToNextInterval(interval = 0) {
  if (interval == 0) return new Date();
  let date = new Date();

  // Get the current minutes
  let minutes = date.getMinutes();

  // Calculate the minutes to add to reach the next interval
  let roundedMinutes = Math.ceil(minutes / interval) * interval;

  // If roundedMinutes reaches 60, we need to increase the hour by 1 and set minutes to 0
  if (roundedMinutes === 60) {
    date.setHours(date.getHours() + 1);
    date.setMinutes(0);
  } else {
    date.setMinutes(roundedMinutes);
  }
  date.setSeconds(0); // Optionally, set seconds to 0

  // Format back to the desired time string, e.g., "2024-11-06T12:15"
  return date;
  //.toISOString().slice(0, 16); // Only the date and time part
}

// Function to update sessionStorage and redirect
export function getTimes(options: any) {
  let startDate, endDate, startTime, endTime;
  const tariff = options.tariff;
  // Handle start-day and start-delay when start-day is 'now'
  if (tariff.type === "daily" || tariff.type === "weekly") {
    let startDateTime = roundUpToNextInterval(options.interval);

    if (options.leadtime) {
      startDateTime = applyHourDelay(startDateTime, options.leadtime);
    }

    startDate = getFormattedDate(startDateTime);
    startTime =
      ("0" + startDateTime.getHours()).slice(-2) +
      ":" +
      ("0" + startDateTime.getMinutes()).slice(-2);

    // Calculate end-date based on length (in days)
    if (tariff.days) {
      let lengthDays = parseInt(tariff.days);
      let endDateTime = new Date(startDateTime);
      endDateTime.setDate(endDateTime.getDate() + lengthDays);
      endDate = getFormattedDate(endDateTime);
      endTime = startTime;
    } else {
      endDate = startDate;
      endTime = startTime;
    }
  }

  // Handle start-day and end-day when specific weekdays are given
  //   else if (params["start-day"] && params["end-day"]) {
  //     let startDateTime = getNextWeekdayDate(params["start-day"]);
  //     startDate = getFormattedDate(startDateTime);
  //     startTime = params["start-hour"]
  //       ? params["start-hour"].replace(/(\d{2})(\d{2})/, "$1:$2")
  //       : null;

  //     let endDateTime = getNextWeekdayDate(params["end-day"], startDateTime);
  //     if (endDateTime <= startDateTime) {
  //       endDateTime.setDate(endDateTime.getDate() + 7);
  //     }
  //     endDate = getFormattedDate(endDateTime);
  //     endTime = params["end-hour"]
  //       ? params["end-hour"].replace(/(\d{2})(\d{2})/, "$1:$2")
  //       : null;
  //   }

  // Retrieve existing user session from sessionStorage
  let userSession = {
    // reservationDate: { start: "", end: "" },
    // reservationTime: { start: "", end: "" },
    selectedStation: "",
    startDate,
    startTime,
    endDate,
    endTime,
  };

  if (startDate) userSession.startDate = startDate;
  if (endDate) userSession.endDate = endDate;
  if (startTime) userSession.startTime = startTime;
  if (endTime) userSession.endTime = endTime;

  userSession.selectedStation = options.stationpreset;

  return userSession;
}

export function CalcTariff({
  id,
  options,
  error,
  success,
  setShowModal,
  vehicle,
}: {
  id: string;
  options: any;
  vehicle: Vehicle;
  error: (id: string) => void;
  success: (id: string) => void;
  setShowModal: (bool: boolean) => void;
}) {
  const dispatch = useDispatch();
  const userSession = useSelector((state: RootState) => state.userSession);
  //   console.log(options);
  const [enabledstations, setEnabledstations] = useState<string[] | undefined>(
    undefined,
  );
  const [time, setTime] = useState<any>(null);

  const type = options.tariff.type || "";
  // Direct access to the price value (string/number or null)
  const price = userSession.tariffs?.[id]?.[type];
  const isLoading = userSession.tariffsLoading?.[id]?.[type];

  useEffect(() => {
    setTime(getTimes(options));
  }, []);

  useEffect(() => {
    if (!id) return;
    if (!time) return;
    // console.log(userSession.tariffs?.[id]?.[type]);

    // Check if we already have data OR are currently loading
    if (userSession.tariffs?.[id]?.[type] !== undefined) {
      if (userSession.tariffs?.[id]?.[type].price === "") {
        error(type);
      }
      return;
    }

    if (userSession.tariffsLoading?.[id]?.[type]) {
      return;
    }

    dispatch(setTariffsLoading({ id, type }));

    ormCalculations
      .calculate(time, false, id)
      .then((res) => {
        if (res === "") {
          error(type);
        }
        dispatch(setTariffsSuccess({ id, type, data: res }));
      })
      .catch((err) => {
        console.error(`Calculation failed for ${id}:`, err);
        error(type);
        dispatch(setTariffsError({ id, type }));
      });
  }, [id, type, time, dispatch]); // Füge dependencies hinzu, aber entferne isLoading!

  async function setCar(cargroup?: Vehicle) {
    if (cargroup) {
      setEnabledstations(cargroup.enabled_stations);

      const categories = cargroup!.vehicle_category!.map((item) => item.id);
      console.log(time);

      await ormStationsService.fetchOpeningTimes({
        reservationDate: { start: time.startDate, end: time.endDate },
        reservationTime: { start: time.startTime, end: time.endTime },
        selectedStation: time.selectedStation,
      } as any);

      dispatch(
        setSelectedVehicle({
          vehicleId: cargroup.id,
          categories: categories,
        }),
      );
      dispatch(
        setSelectedStation({ type: "start", stationId: time.selectedStation }),
      );
    }

    setShowModal(true);
  }

  if (isLoading) {
    return (
      <div className="orm-carprice orm-carprice--loading">
        <span className="orm-price__loader"></span>
      </div>
    );
  }

  // If price is null or undefined, we don't render
  if (price === null || price === undefined || price.price === "") {
    return null;
  }

  return (
    <div className="orm-tariff-card__pricebox">
      <div className="orm-carprice orm-carprice--start orm-mb-4">
        <div className="orm-price__txt__surtitle">
          {ORMfunctions.i18n("cargroup/unitbefore")} {price.unit}{" "}
          {ORMfunctions.i18n("cargroup/unitafter")}
        </div>
        <div className="orm-price__txt">{convertCurrency(price.price)}</div>
      </div>
      <button
        className="orm-button__cta colored orm-btn__block"
        onClick={() => setCar(vehicle)}
      >
        {ORMfunctions.i18n("general/showOffers") || "Angebote zeigen"}
      </button>
    </div>
  );
}

export default CalcTariff;
